// src/context/AppContext.jsx
import React, { createContext, useContext, useReducer, useEffect } from 'react';

const AppContext = createContext();
const APP_STORAGE_KEY = 'pontso_yarona_app_state';

// App reducer
const appReducer = (state, action) => {
  switch (action.type) {
    case 'LOAD_APP_STATE':
      return {
        ...state,
        ...action.payload,
        isLoaded: true
      };

    case 'SET_USER_PREFERENCES':
      return {
        ...state,
        userPreferences: {
          ...state.userPreferences,
          ...action.payload
        }
      };

    case 'SET_THEME':
      return {
        ...state,
        theme: action.payload
      };

    case 'SET_CURRENCY':
      return {
        ...state,
        currency: action.payload
      };

    case 'SET_LANGUAGE':
      return {
        ...state,
        language: action.payload
      };

    case 'TOGGLE_SIDEBAR':
      return {
        ...state,
        ui: {
          ...state.ui,
          sidebarOpen: !state.ui.sidebarOpen
        }
      };

    case 'SET_LOADING':
      return {
        ...state,
        ui: {
          ...state.ui,
          loading: action.payload
        }
      };

    case 'SET_NOTIFICATION':
      return {
        ...state,
        ui: {
          ...state.ui,
          notification: action.payload
        }
      };

    case 'CLEAR_NOTIFICATION':
      return {
        ...state,
        ui: {
          ...state.ui,
          notification: null
        }
      };

    case 'SET_SEARCH_HISTORY':
      const updatedHistory = [action.payload, ...state.searchHistory.filter(item => item !== action.payload)].slice(0, 10);
      return {
        ...state,
        searchHistory: updatedHistory
      };

    case 'CLEAR_SEARCH_HISTORY':
      return {
        ...state,
        searchHistory: []
      };

    case 'SET_RECENTLY_VIEWED':
      const updatedRecentlyViewed = [
        action.payload,
        ...state.recentlyViewed.filter(item => item.id !== action.payload.id)
      ].slice(0, 20);
      return {
        ...state,
        recentlyViewed: updatedRecentlyViewed
      };

    case 'SET_FILTER_PREFERENCES':
      return {
        ...state,
        filterPreferences: {
          ...state.filterPreferences,
          ...action.payload
        }
      };

    case 'ADD_FAVORITE_FILTER':
      return {
        ...state,
        favoriteFilters: [
          ...state.favoriteFilters,
          { id: Date.now(), name: action.payload.name, filters: action.payload.filters }
        ]
      };

    case 'REMOVE_FAVORITE_FILTER':
      return {
        ...state,
        favoriteFilters: state.favoriteFilters.filter(filter => filter.id !== action.payload.id)
      };

    default:
      return state;
  }
};

// Initial state
const initialState = {
  // User preferences
  userPreferences: {
    emailNotifications: true,
    pushNotifications: false,
    marketingEmails: true,
    currency: 'ZAR',
    language: 'en',
    theme: 'light'
  },
  
  // App settings
  theme: 'light',
  currency: 'ZAR',
  language: 'en',
  
  // UI state
  ui: {
    sidebarOpen: false,
    loading: false,
    notification: null
  },
  
  // User behavior tracking
  searchHistory: [],
  recentlyViewed: [],
  filterPreferences: {
    defaultSort: 'name',
    defaultView: 'grid',
    rememberFilters: true
  },
  favoriteFilters: [],
  
  // App metadata
  isLoaded: false,
  lastUpdated: null
};

export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Load app state from localStorage on mount
  useEffect(() => {
    try {
      const savedState = localStorage.getItem(APP_STORAGE_KEY);
      if (savedState) {
        const parsedState = JSON.parse(savedState);
        dispatch({
          type: 'LOAD_APP_STATE',
          payload: parsedState
        });
      } else {
        dispatch({
          type: 'LOAD_APP_STATE',
          payload: {}
        });
      }
    } catch (error) {
      console.error('Error loading app state from localStorage:', error);
      dispatch({
        type: 'LOAD_APP_STATE',
        payload: {}
      });
    }
  }, []);

  // Save app state to localStorage whenever it changes
  useEffect(() => {
    if (state.isLoaded) {
      try {
        const stateToSave = {
          userPreferences: state.userPreferences,
          theme: state.theme,
          currency: state.currency,
          language: state.language,
          searchHistory: state.searchHistory,
          recentlyViewed: state.recentlyViewed,
          filterPreferences: state.filterPreferences,
          favoriteFilters: state.favoriteFilters,
          lastUpdated: new Date().toISOString()
        };
        localStorage.setItem(APP_STORAGE_KEY, JSON.stringify(stateToSave));
      } catch (error) {
        console.error('Error saving app state to localStorage:', error);
      }
    }
  }, [state, state.isLoaded]);

  // App actions
  const setUserPreferences = (preferences) => {
    dispatch({
      type: 'SET_USER_PREFERENCES',
      payload: preferences
    });
  };

  const setTheme = (theme) => {
    dispatch({
      type: 'SET_THEME',
      payload: theme
    });
    
    // Apply theme to document
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  const setCurrency = (currency) => {
    dispatch({
      type: 'SET_CURRENCY',
      payload: currency
    });
  };

  const setLanguage = (language) => {
    dispatch({
      type: 'SET_LANGUAGE',
      payload: language
    });
  };

  // UI actions
  const toggleSidebar = () => {
    dispatch({ type: 'TOGGLE_SIDEBAR' });
  };

  const setLoading = (loading) => {
    dispatch({
      type: 'SET_LOADING',
      payload: loading
    });
  };

  const showNotification = (notification) => {
    dispatch({
      type: 'SET_NOTIFICATION',
      payload: {
        id: Date.now(),
        ...notification,
        timestamp: new Date().toISOString()
      }
    });

    // Auto-clear notification after delay
    if (notification.autoClose !== false) {
      setTimeout(() => {
        clearNotification();
      }, notification.duration || 5000);
    }
  };

  const clearNotification = () => {
    dispatch({ type: 'CLEAR_NOTIFICATION' });
  };

  // Search and history actions
  const addToSearchHistory = (searchTerm) => {
    if (searchTerm.trim()) {
      dispatch({
        type: 'SET_SEARCH_HISTORY',
        payload: searchTerm.trim()
      });
    }
  };

  const clearSearchHistory = () => {
    dispatch({ type: 'CLEAR_SEARCH_HISTORY' });
  };

  const addToRecentlyViewed = (product) => {
    dispatch({
      type: 'SET_RECENTLY_VIEWED',
      payload: {
        id: product.id,
        name: product.name,
        image: product.image,
        price: product.price,
        category: product.category,
        viewedAt: new Date().toISOString()
      }
    });
  };

  // Filter preferences
  const setFilterPreferences = (preferences) => {
    dispatch({
      type: 'SET_FILTER_PREFERENCES',
      payload: preferences
    });
  };

  const saveFavoriteFilter = (name, filters) => {
    dispatch({
      type: 'ADD_FAVORITE_FILTER',
      payload: { name, filters }
    });
  };

  const removeFavoriteFilter = (filterId) => {
    dispatch({
      type: 'REMOVE_FAVORITE_FILTER',
      payload: { id: filterId }
    });
  };

  // Utility functions
  const formatCurrency = (amount) => {
    const currencySymbols = {
      ZAR: 'R',
      USD: '$',
      EUR: '€',
      GBP: '£'
    };
    
    return `${currencySymbols[state.currency] || 'R'}${amount.toFixed(2)}`;
  };

  const getRecentlyViewedProducts = (limit = 10) => {
    return state.recentlyViewed.slice(0, limit);
  };

  const getSearchSuggestions = (query, limit = 5) => {
    return state.searchHistory
      .filter(term => term.toLowerCase().includes(query.toLowerCase()))
      .slice(0, limit);
  };

  // Apply theme on mount
  useEffect(() => {
    if (state.theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [state.theme]);

  const value = {
    // State
    userPreferences: state.userPreferences,
    theme: state.theme,
    currency: state.currency,
    language: state.language,
    ui: state.ui,
    searchHistory: state.searchHistory,
    recentlyViewed: state.recentlyViewed,
    filterPreferences: state.filterPreferences,
    favoriteFilters: state.favoriteFilters,
    isLoaded: state.isLoaded,
    
    // Actions
    setUserPreferences,
    setTheme,
    setCurrency,
    setLanguage,
    
    // UI actions
    toggleSidebar,
    setLoading,
    showNotification,
    clearNotification,
    
    // Search and history
    addToSearchHistory,
    clearSearchHistory,
    addToRecentlyViewed,
    
    // Filter preferences
    setFilterPreferences,
    saveFavoriteFilter,
    removeFavoriteFilter,
    
    // Utilities
    formatCurrency,
    getRecentlyViewedProducts,
    getSearchSuggestions
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

// Specific hooks for different parts of the app state
export const useTheme = () => {
  const { theme, setTheme } = useApp();
  return { theme, setTheme };
};

export const useNotifications = () => {
  const { ui, showNotification, clearNotification } = useApp();
  return {
    notification: ui.notification,
    showNotification,
    clearNotification
  };
};

export const useSearchHistory = () => {
  const { searchHistory, addToSearchHistory, clearSearchHistory, getSearchSuggestions } = useApp();
  return {
    searchHistory,
    addToSearchHistory,
    clearSearchHistory,
    getSearchSuggestions
  };
};

export const useRecentlyViewed = () => {
  const { recentlyViewed, addToRecentlyViewed, getRecentlyViewedProducts } = useApp();
  return {
    recentlyViewed,
    addToRecentlyViewed,
    getRecentlyViewedProducts
  };
};